import {
  isOperation,
  type AnalyticsEvent,
  type AnalyticsEventName,
  type AnalyticsOutcome,
  type InputFormat,
  type Operation,
  type OutputFormat,
} from "./types"

type Tracker = {
  track: (name: string, properties?: Record<string, string>) => unknown
}

const EVENT_NAMES: readonly AnalyticsEventName[] = [
  "job_completed",
  "job_error",
  "result_copied",
  "result_downloaded",
  "return_visit",
]

const OUTCOMES: readonly AnalyticsOutcome[] = ["success", "warning", "error"]
const INPUT_FORMATS: readonly InputFormat[] = ["json", "yaml"]
const OUTPUT_FORMATS: readonly Exclude<OutputFormat, "none">[] = ["json", "yaml"]

const PROPERTY_NAMES = new Set([
  "operation",
  "input_format",
  "output_format",
  "outcome",
])

function isEventName(value: string): value is AnalyticsEventName {
  return EVENT_NAMES.includes(value as AnalyticsEventName)
}

function isOutcome(value: string): value is AnalyticsOutcome {
  return OUTCOMES.includes(value as AnalyticsOutcome)
}

function isInputFormat(value: string): value is InputFormat {
  return INPUT_FORMATS.includes(value as InputFormat)
}

function isOutputFormat(value: string): value is Exclude<OutputFormat, "none"> {
  return OUTPUT_FORMATS.includes(value as Exclude<OutputFormat, "none">)
}

function tracker(): Tracker | null {
  const candidate = (globalThis as { umami?: unknown }).umami
  if (!candidate || typeof candidate !== "object") {
    return null
  }

  const track = (candidate as { track?: unknown }).track
  return typeof track === "function" ? (candidate as Tracker) : null
}

export function validatedEvent(event: AnalyticsEvent): {
  name: AnalyticsEventName
  properties?: Record<string, string>
} | null {
  if (!isEventName(event.name)) {
    return null
  }

  const entries = Object.entries(event).filter(([key]) => key !== "name")

  if (event.name === "return_visit" && entries.length > 0) {
    return null
  }

  const properties: Record<string, string> = {}

  for (const [key, rawValue] of entries) {
    if (!PROPERTY_NAMES.has(key) || typeof rawValue !== "string") {
      return null
    }

    if (key === "operation" && !isOperation(rawValue)) {
      return null
    }

    if (key === "input_format" && !isInputFormat(rawValue)) {
      return null
    }

    if (key === "output_format" && !isOutputFormat(rawValue)) {
      return null
    }

    if (key === "outcome" && !isOutcome(rawValue)) {
      return null
    }

    properties[key] = rawValue
  }

  return {
    name: event.name,
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
  }
}

export function trackEvent(event: AnalyticsEvent): boolean {
  const validated = validatedEvent(event)
  const currentTracker = validated ? tracker() : null

  if (!validated || !currentTracker) {
    return false
  }

  try {
    currentTracker.track(validated.name, validated.properties)
    return true
  } catch {
    return false
  }
}

let returnVisitSent = false
let lifecycleInstalled = false

export function trackReturnVisit(): boolean {
  if (returnVisitSent) {
    return true
  }

  const sent = trackEvent({ name: "return_visit" })
  if (sent) {
    returnVisitSent = true
  }
  return sent
}

export function installAnalyticsLifecycle(): void {
  if (lifecycleInstalled) {
    return
  }

  lifecycleInstalled = true

  const emit = () => {
    trackReturnVisit()
  }

  if (document.readyState === "complete") {
    emit()
  } else {
    window.addEventListener("load", emit, { once: true })
  }

  document.addEventListener("turbo:load", emit)
}

export function operationEvent(
  name: "job_completed" | "job_error",
  operation: Operation,
  inputFormat: InputFormat,
  outputFormat: OutputFormat,
  outcome: AnalyticsOutcome,
): AnalyticsEvent {
  return {
    name,
    operation,
    input_format: inputFormat,
    outcome,
    ...(outputFormat !== "none" ? { output_format: outputFormat } : {}),
  }
}
